import { createSignal, createMemo, createEffect, onCleanup, onMount, Show, For } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import { useSFetch } from '../hooks/sfetch';
import PingBadge from '../components/PingBadge';
import Sparkline from '../components/Sparkline';
import Text from '../components/Text';
import type { ProxyInfo } from '../services/proxyApi';
import styles from './BotEngine.module.css';

interface BroadcasterStat {
    botId: string;
    subscribersReadOnly: number;
    subscribersFullControl: number;
    maxChannelDepth: number;
    packetsForwardedTotal: number;
    bytesForwardedTotal: number;
    positionBroadcastsTotal: number;
    packetsDroppedTotal: number;
}

interface ResumeCurrent {
    botRegistryId: string;
    minecraftUsername: string;
    attemptNumber: number;
    maxAttempts: number;
    phase: string;
    nextActionAt: string | null;
}

interface ResumeOutcome {
    botRegistryId: string;
    minecraftUsername: string;
    outcome: string;
    attemptsUsed: number;
    reason: string | null;
    finishedAt: string;
}

interface ResumeSnapshot {
    isRunning: boolean;
    startedAt: string | null;
    completedAt: string | null;
    total: number;
    succeeded: number;
    failed: number;
    pending: number;
    current: ResumeCurrent | null;
    recent: ResumeOutcome[];
}

interface ShutdownCurrent {
    botRegistryId: string;
    minecraftUsername: string;
    order: number;
    total: number;
    phase: string;
    nextActionAt: string | null;
}

interface ShutdownOutcome {
    botRegistryId: string;
    minecraftUsername: string;
    outcome: string;
    reason: string | null;
    finishedAt: string;
}

interface ShutdownSnapshot {
    isShuttingDown: boolean;
    startedAt: string | null;
    completedAt: string | null;
    total: number;
    drained: number;
    failed: number;
    pending: number;
    current: ShutdownCurrent | null;
    recent: ShutdownOutcome[];
}

interface WorkerStat {
    workerId: number;
    botCount: number;
    averageTickMs: number;
    peakTickMs: number;
    realTps: number;
    gapAvgMs: number;
    gapP95Ms: number;
    gapMaxMs: number;
    oversleepAvgMs: number;
    oversleepP95Ms: number;
    tickBudgetUsage: number;
    isOverloaded: boolean;
}

interface BotStat {
    botRegistryId: string;
    username: string;
    server: string;
    connectedSince: string;
    packetsInPerSecond: number;
    packetsOutPerSecond: number;
    chunksLoaded: number;
    entitiesTracked: number;
    ping: number;
    state: string;
    inboundQueueDepth: number;
    packetsDrainedPerTick: number;
    tickProcessingMs: number;
    frameIntervalMs: number;
    frameDecodeMs: number;
    socketBytesAvailable: number;
    proxyId: string | null;
    averageTickMs: number;
    peakTickMs: number;
    tickJitterMs: number;
    averageQueueDepth: number;
    averageDrainPerTick: number;
    averageFrameIntervalMs: number;
    frameIntervalP95Ms: number;
    averageFrameDecodeMs: number;
    sampleWindowSize: number;
}

interface EngineStats {
    uptime: string;
    workerCount: number;
    totalBots: number;
    totalPacketsProcessed: number;
    totalPacketsPerSecond: number;
    totalInboundQueueDepth: number;
    botsReconnecting: number;
    workers: WorkerStat[];
    bots: BotStat[];
}

const PROXY_POLL_INTERVAL_MS = 10_000;

// Health thresholds for the saturation-aware metrics. Real TPS / gap / oversleep replace the old
// work-region tick numbers, which stayed green even when the host was CPU-starved. Frame-read bounds
// are initial guesses to be dialed in against real production numbers.
const REAL_TPS_GREEN = 19.5;
const REAL_TPS_YELLOW = 17;
const GAP_P95_GREEN_MS = 55;
const GAP_P95_YELLOW_MS = 70;
const OVERSLEEP_P95_GREEN_MS = 3;
const OVERSLEEP_P95_YELLOW_MS = 15;
const FRAME_INTERVAL_P95_GREEN_MS = 1500;
const FRAME_INTERVAL_P95_YELLOW_MS = 2500;

const realTpsColor = (tps: number) => {
    if (tps >= REAL_TPS_GREEN) return 'var(--success-500)';
    if (tps >= REAL_TPS_YELLOW) return 'var(--warning-500)';
    return 'var(--error-500)';
};

const gapColor = (p95: number) => {
    if (p95 <= GAP_P95_GREEN_MS) return 'var(--success-500)';
    if (p95 <= GAP_P95_YELLOW_MS) return 'var(--warning-500)';
    return 'var(--error-500)';
};

const oversleepColor = (p95: number) => {
    if (p95 <= OVERSLEEP_P95_GREEN_MS) return 'var(--success-500)';
    if (p95 <= OVERSLEEP_P95_YELLOW_MS) return 'var(--warning-500)';
    return 'var(--error-500)';
};

const frameIntervalColor = (p95: number) => {
    if (p95 <= FRAME_INTERVAL_P95_GREEN_MS) return 'var(--success-500)';
    if (p95 <= FRAME_INTERVAL_P95_YELLOW_MS) return 'var(--warning-500)';
    return 'var(--error-500)';
};

const queueColor = (depth: number) => {
    if (depth > 50) return 'var(--error-500)';
    if (depth > 10) return '#f59e0b';
    return 'var(--success-500)';
};

const tickColor = (ms: number) => {
    if (ms > 10) return 'var(--error-500)';
    if (ms > 5) return '#f59e0b';
    return 'var(--dark-text-primary)';
};

const stateColor = (state: string) => {
    switch (state) {
        case 'Playing': return 'var(--success-500)';
        case 'Connecting': case 'Reconnecting': return '#f59e0b';
        case 'Error': return 'var(--error-500)';
        default: return 'var(--dark-text-muted)';
    }
};

const botHealth = (b: BotStat): 'healthy' | 'warning' | 'critical' => {
    // 10s averages match displayed numbers; last-tick spikes would mismatch border/values.
    if (b.averageQueueDepth > 50 || b.averageTickMs > 10) return 'critical';
    if (b.averageQueueDepth > 10 || b.averageTickMs > 5) return 'warning';
    return 'healthy';
};

const fmt = (n: number, decimals = 1) => n.toFixed(decimals);

const fmtBytes = (b: number) => {
    if (b < 1024) return `${b}B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
    return `${(b / 1024 / 1024).toFixed(1)}MB`;
};

export default function BotEngineStats() {
    const api = useApi();
    const sfetch = useSFetch();
    const [engineStats, setEngineStats] = createStore<{ current: EngineStats | null }>({ current: null });
    const stats = () => engineStats.current;
    const [broadcasterStats, setBroadcasterStats] = createStore<BroadcasterStat[]>([]);
    const [online, setOnline] = createSignal(true);
    const [lastUpdated, setLastUpdated] = createSignal<Date | null>(null);
    const [proxies, setProxies] = createStore<ProxyInfo[]>([]);

    const HISTORY_MAX = 900;
    const [systemQueue, setSystemQueue] = createSignal<number[]>([]);
    let lastSystemTickAt = 0;

    const queueYMax = createMemo(() => {
        const q = systemQueue();
        if (q.length === 0) return 1;
        let max = 1;
        for (const v of q) if (v > max) max = v;
        return max;
    });

    const worstRealTps = createMemo(() => {
        const w = stats()?.workers ?? [];
        if (w.length === 0) return null;
        let min = Infinity;
        for (const x of w) if (x.realTps < min) min = x.realTps;
        return min;
    });

    const proxyById = () => {
        const map = new Map<string, ProxyInfo>();
        for (const p of proxies) map.set(p.id, p);
        return map;
    };

    const fetchProxies = async () => {
        const res = await api.proxy.getAll();
        if (isOk(res.data)) setProxies(reconcile(res.data.value.proxies as unknown as ProxyInfo[], { key: 'id' }));
    };

    const fetchSystemQueueHistory = async () => {
        const res = await api.botStats.getSystemQueueHistory();
        if (isOk(res.data)) {
            setSystemQueue((res.data.value.history ?? []).slice(-HISTORY_MAX));
            lastSystemTickAt = Date.now();
        }
    };

    const appendLiveSamples = (frame: EngineStats) => {
        const now = Date.now();
        if (now - lastSystemTickAt < 1000) return;
        lastSystemTickAt = now;
        const existing = systemQueue();
        const appended = existing.length >= HISTORY_MAX
            ? [...existing.slice(existing.length - HISTORY_MAX + 1), frame.totalInboundQueueDepth]
            : [...existing, frame.totalInboundQueueDepth];
        setSystemQueue(appended);
    };

    let eventSource: EventSource | undefined;
    let reconnectTimer: number | undefined;

    const connectStream = () => {
        eventSource?.close();
        const es = new EventSource('/api/botengine/stats/stream');
        eventSource = es;

        es.addEventListener('open', () => setOnline(true));
        es.addEventListener('message', (e: MessageEvent) => {
            try {
                const parsed = JSON.parse(e.data) as EngineStats;
                setEngineStats('current', reconcile(parsed, { key: 'botRegistryId' }));
                setOnline(true);
                setLastUpdated(new Date());
                appendLiveSamples(parsed);
            } catch { }
        });
        es.addEventListener('error', () => {
            setOnline(false);
            // Guard: flapping streams leak listeners if we queue parallel reconnect timers.
            if (es.readyState === EventSource.CLOSED && reconnectTimer == null) {
                reconnectTimer = window.setTimeout(() => {
                    reconnectTimer = undefined;
                    connectStream();
                }, 2000);
            }
        });
    };

    const [resume, setResume] = createSignal<ResumeSnapshot | null>(null);
    let resumeHandle: number | undefined;
    const RESUME_POLL_MS = 2000;
    const RESUME_STICKY_MS = 60_000;

    const fetchResumeStatus = async () => {
        try {
            const res = await api.botStats.getAutoResumeStatus();
            if (isOk(res.data)) setResume(res.data.value as unknown as ResumeSnapshot);
        } catch { }
    };

    const resumeVisible = createMemo(() => {
        const r = resume();
        if (!r) return false;
        if (r.isRunning) return true;
        if (r.completedAt && r.total > 0) {
            const finished = new Date(r.completedAt).getTime();
            return (Date.now() - finished) < RESUME_STICKY_MS;
        }
        return false;
    });

    const [shutdown, setShutdown] = createSignal<ShutdownSnapshot | null>(null);
    let shutdownHandle: number | undefined;
    const SHUTDOWN_POLL_MS = 2000;
    const SHUTDOWN_STICKY_MS = 60_000;

    const fetchBroadcasterStats = async () => {
        try {
            const res = await sfetch('/api/vektor/admin/possessions/stats');
            if (res?.ok && res.value?.bots) {
                setBroadcasterStats(reconcile(res.value.bots as BroadcasterStat[], { key: 'botId' }));
            }
        } catch { /* polling — swallow */ }
    };

    const fetchShutdownStatus = async () => {
        try {
            const res = await api.botStats.getShutdownStatus();
            if (isOk(res.data)) setShutdown(res.data.value as unknown as ShutdownSnapshot);
        } catch { }
    };

    const shutdownVisible = createMemo(() => {
        const s = shutdown();
        if (!s) return false;
        if (s.isShuttingDown) return true;
        if (s.completedAt && s.total > 0) {
            const finished = new Date(s.completedAt).getTime();
            return (Date.now() - finished) < SHUTDOWN_STICKY_MS;
        }
        return false;
    });

    onMount(() => {
        fetchSystemQueueHistory();
        connectStream();
        fetchProxies();
        const proxyHandle = window.setInterval(fetchProxies, PROXY_POLL_INTERVAL_MS);

        void fetchResumeStatus();
        resumeHandle = window.setInterval(() => {
            const r = resume();
            if (r && !r.isRunning && r.completedAt) {
                const finished = new Date(r.completedAt).getTime();
                if ((Date.now() - finished) >= RESUME_STICKY_MS) {
                    if (resumeHandle != null) { clearInterval(resumeHandle); resumeHandle = undefined; }
                    return;
                }
            }
            void fetchResumeStatus();
        }, RESUME_POLL_MS);

        void fetchShutdownStatus();
        shutdownHandle = window.setInterval(() => {
            void fetchShutdownStatus();
        }, SHUTDOWN_POLL_MS);

        void fetchBroadcasterStats();
        const broadcasterHandle = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            void fetchBroadcasterStats();
        }, 3000);

        onCleanup(() => {
            clearInterval(proxyHandle);
            if (resumeHandle != null) clearInterval(resumeHandle);
            if (shutdownHandle != null) clearInterval(shutdownHandle);
            clearInterval(broadcasterHandle);
        });
    });

    onCleanup(() => {
        eventSource?.close();
        if (reconnectTimer != null) clearTimeout(reconnectTimer);
    });

    const [now, setNow] = createSignal(Date.now());
    createEffect(() => {
        const h = window.setInterval(() => setNow(Date.now()), 1000);
        onCleanup(() => clearInterval(h));
    });

    const agoText = () => {
        const u = lastUpdated();
        if (!u) return 'never';
        const diffSec = Math.max(0, Math.round((now() - u.getTime()) / 1000));
        if (diffSec < 2) return 'just now';
        if (diffSec < 60) return `${diffSec}s ago`;
        const m = Math.floor(diffSec / 60);
        return `${m}m ${diffSec % 60}s ago`;
    };

    const BotHealthCard = (props: { bot: BotStat }) => {
        const b = () => props.bot;
        const health = () => botHealth(b());
        const healthAttr = () => health() === 'healthy' ? undefined : health();
        const proxy = () => b().proxyId ? proxyById().get(b().proxyId!) ?? null : null;

        return (
            <div class={styles.botHealthCard} data-health={healthAttr()}>
                <div class={styles.botHealthHeader}>
                    <div class={styles.botHealthDot} style={{ background: stateColor(b().state) }} />
                    <span class={styles.botHealthName}>{b().username}</span>
                    <span class={styles.botHealthServer}>{b().server}</span>
                </div>

                <div style={{
                    display: 'flex',
                    'align-items': 'center',
                    gap: '8px',
                    'font-size': 'var(--font-size-xs)',
                    color: 'var(--dark-text-muted)',
                    padding: '4px 0',
                    'flex-wrap': 'wrap',
                }}>
                    <i class="fa-solid fa-network-wired" />
                    <Show when={b().proxyId && proxy()} fallback={
                        <Show when={b().proxyId} fallback={<span><Text val="PROXY_DIRECT_TITLE" /></span>}>
                            <span style={{ 'font-family': 'monospace' }}>
                                proxy {b().proxyId!.slice(0, 8)}...
                            </span>
                        </Show>
                    }>
                        <span style={{ color: 'var(--dark-text-secondary)', 'font-weight': 'bold' }}>
                            {proxy()!.name}
                        </span>
                        <span style={{ 'font-family': 'monospace' }}>
                            {proxy()!.host}:{proxy()!.port}
                        </span>
                        <PingBadge ms={proxy()!.lastPingMs} compact />
                    </Show>
                </div>

                <div class={styles.botHealthMetrics}>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>
                            Queue <span style={{ color: queueColor(b().inboundQueueDepth), 'font-family': 'monospace' }}>
                                {b().inboundQueueDepth}
                            </span>
                            <span style={{ color: 'var(--dark-text-muted)' }}> · avg</span>
                        </span>
                        <span
                            class={styles.botHealthMetricValue}
                            style={{ color: queueColor(b().averageQueueDepth), transition: 'color 0.4s' }}
                            title={`Live ${b().inboundQueueDepth} · 10s avg ${fmt(b().averageQueueDepth, 1)}`}
                        >
                            {fmt(b().averageQueueDepth, 1)}
                        </span>
                        <div class={styles.botHealthBar}>
                            <div class={styles.botHealthBarFill} style={{
                                width: `${Math.min(100, b().averageQueueDepth * 2)}%`,
                                background: queueColor(b().averageQueueDepth),
                                transition: 'width 0.4s ease-out, background-color 0.4s',
                            }} />
                        </div>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Drain/t (avg)</span>
                        <span
                            class={styles.botHealthMetricValue}
                            title={`Live: ${b().packetsDrainedPerTick}`}
                        >
                            {fmt(b().averageDrainPerTick, 1)}
                        </span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Tick (avg)</span>
                        <span
                            class={styles.botHealthMetricValue}
                            style={{ color: tickColor(b().averageTickMs), transition: 'color 0.4s' }}
                            title={`Live: ${fmt(b().tickProcessingMs, 2)}ms · peak ${fmt(b().peakTickMs, 2)}ms · jitter ${fmt(b().tickJitterMs, 2)}ms`}
                        >
                            {fmt(b().averageTickMs, 2)}ms
                        </span>
                        <div class={styles.botHealthBar}>
                            <div class={styles.botHealthBarFill} style={{
                                width: `${Math.min(100, b().averageTickMs / 10 * 100)}%`,
                                background: tickColor(b().averageTickMs),
                                transition: 'width 0.4s ease-out, background-color 0.4s',
                            }} />
                        </div>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Ping</span>
                        <span class={styles.botHealthMetricValue}>{b().ping}ms</span>
                    </div>
                </div>

                <hr class={styles.botHealthDivider} />

                <div class={styles.botHealthMetrics}>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Pkt In/s</span>
                        <span class={styles.botHealthMetricValue}>{b().packetsInPerSecond}</span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Pkt Out/s</span>
                        <span class={styles.botHealthMetricValue}>{b().packetsOutPerSecond}</span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Chunks</span>
                        <span class={styles.botHealthMetricValue}>{b().chunksLoaded}</span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Entities</span>
                        <span class={styles.botHealthMetricValue}>{b().entitiesTracked}</span>
                    </div>
                </div>

                <hr class={styles.botHealthDivider} />

                <div class={styles.botHealthMetrics}>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Frame Decode (avg)</span>
                        <span class={styles.botHealthMetricValue} title={`Real decrypt+parse work, blocking socket wait excluded. Live ${fmt(b().frameDecodeMs, 2)}ms`}>
                            {fmt(b().averageFrameDecodeMs, 2)}ms
                        </span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Frame Interval (avg/p95)</span>
                        <span
                            class={styles.botHealthMetricValue}
                            style={{ color: frameIntervalColor(b().frameIntervalP95Ms), transition: 'color 0.4s' }}
                            title={`Wall-clock per frame, dominated by idle wait for the next packet. Live ${fmt(b().frameIntervalMs, 2)}ms · avg ${fmt(b().averageFrameIntervalMs, 2)}ms · p95 ${fmt(b().frameIntervalP95Ms, 2)}ms (windowed, last 200 frames)`}
                        >
                            {fmt(b().averageFrameIntervalMs, 2)} / {fmt(b().frameIntervalP95Ms, 2)}ms
                        </span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>Socket Buf</span>
                        <span class={styles.botHealthMetricValue}>{fmtBytes(b().socketBytesAvailable)}</span>
                    </div>
                    <div class={styles.botHealthMetric}>
                        <span class={styles.botHealthMetricLabel}>State</span>
                        <span class={styles.botHealthMetricValue} style={{ color: stateColor(b().state) }}>
                            {b().state}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <h1><i class="fa-solid fa-chart-bar" /> Bot Engine Statistics</h1>
            </div>

            <Show when={!online()}>
                <div class={styles.offlineBanner}>
                    <i class="fa-solid fa-triangle-exclamation" />
                    <span>Service temporarily unavailable. Reconnecting...</span>
                </div>
            </Show>

            <Show when={resumeVisible() && resume()}>
                {(() => {
                    const r = () => resume()!;
                    const pct = () => r().total === 0 ? 0 : Math.round(((r().succeeded + r().failed) / r().total) * 100);
                    const phaseLabel = (phase: string) => {
                        switch (phase) {
                            case 'Starting':              return 'BOT_AUTORESUME_PHASE_STARTING';
                            case 'WaitingForRetry':       return 'BOT_AUTORESUME_PHASE_WAITING_RETRY';
                            case 'WaitingBeforeNextBot':  return 'BOT_AUTORESUME_PHASE_WAITING_NEXT';
                            default:                      return 'BOT_AUTORESUME_PHASE_IDLE';
                        }
                    };
                    const secondsUntil = (iso: string | null): string => {
                        if (!iso) return '';
                        const diff = Math.max(0, Math.round((new Date(iso).getTime() - now()) / 1000));
                        return `${diff}s`;
                    };
                    const outcomeClass = (outcome: string) => {
                        if (outcome === 'Succeeded') return styles.autoResumeOutcomeSuccess;
                        if (outcome === 'Failed')    return styles.autoResumeOutcomeFailed;
                        return styles.autoResumeOutcomeSkipped;
                    };

                    return (
                        <div class={styles.autoResumeCard}>
                            <div class={styles.autoResumeHeader}>
                                <h2 style={{ margin: 0 }}>
                                    <i class="fa-solid fa-rotate" style={{ 'margin-right': '8px' }} />
                                    <Text val="BOT_AUTORESUME_TITLE" />
                                </h2>
                                <Show when={r().isRunning} fallback={
                                    <span class={styles.autoResumePill} data-state="done">
                                        <i class="fa-solid fa-circle-check" />
                                        <Text val="BOT_AUTORESUME_DONE" />
                                    </span>
                                }>
                                    <span class={styles.autoResumePill} data-state="running">
                                        <i class="fa-solid fa-spinner fa-spin" />
                                        <Text val="BOT_AUTORESUME_RUNNING" />
                                    </span>
                                </Show>
                            </div>

                            <div class={styles.autoResumeCounters}>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--dark-text-primary)' }}>
                                        {r().total}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_AUTORESUME_TOTAL" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--success-500)' }}>
                                        {r().succeeded}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_AUTORESUME_SUCCEEDED" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--error-500)' }}>
                                        {r().failed}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_AUTORESUME_FAILED" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--dark-text-secondary)' }}>
                                        {r().pending}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_AUTORESUME_PENDING" /></div>
                                </div>
                            </div>

                            <div class={styles.autoResumeProgress}>
                                <div class={styles.autoResumeProgressFill} style={{ width: `${pct()}%` }} />
                            </div>

                            <Show when={r().current}>
                                {(cur) => (
                                    <div class={styles.autoResumeCurrent}>
                                        <div>
                                            <span class={styles.autoResumeCurrentLabel}><Text val="BOT_AUTORESUME_CURRENT" /></span>
                                            <Show when={cur().botRegistryId && cur().botRegistryId !== '00000000-0000-0000-0000-000000000000'} fallback={
                                                <span class={styles.autoResumeCurrentValue}><Text val="BOT_AUTORESUME_IDLE" /></span>
                                            }>
                                                <span class={styles.autoResumeCurrentValue}>
                                                    {cur().minecraftUsername}
                                                    <span class={styles.autoResumeAttempt}>
                                                        {' '}(attempt {cur().attemptNumber}/{cur().maxAttempts})
                                                    </span>
                                                </span>
                                            </Show>
                                        </div>
                                        <div class={styles.autoResumePhase}>
                                            <Text val={phaseLabel(cur().phase)} />
                                            <Show when={cur().nextActionAt}>
                                                {' · '}{secondsUntil(cur().nextActionAt)}
                                            </Show>
                                        </div>
                                    </div>
                                )}
                            </Show>

                            <Show when={r().recent.length > 0}>
                                <details class={styles.autoResumeRecent}>
                                    <summary><Text val="BOT_AUTORESUME_RECENT" /> ({r().recent.length})</summary>
                                    <div class={styles.autoResumeRecentList}>
                                        <For each={[...r().recent].reverse()}>
                                            {(o) => (
                                                <div class={`${styles.autoResumeRecentRow} ${outcomeClass(o.outcome)}`}>
                                                    <span class={styles.autoResumeRecentUser}>{o.minecraftUsername}</span>
                                                    <span class={styles.autoResumeRecentOutcome}>{o.outcome}</span>
                                                    <span class={styles.autoResumeRecentMeta}>
                                                        <Show when={o.attemptsUsed > 0}>{o.attemptsUsed}× </Show>
                                                        <Show when={o.reason}>{o.reason}</Show>
                                                    </span>
                                                </div>
                                            )}
                                        </For>
                                    </div>
                                </details>
                            </Show>

                            <div class={styles.autoResumeRepassHint}>
                                <i class="fa-solid fa-rotate" />
                                <Text val="BOT_AUTORESUME_REPASS_HINT" />
                            </div>
                        </div>
                    );
                })()}
            </Show>

            <Show when={shutdownVisible() && shutdown()}>
                {(() => {
                    const s = () => shutdown()!;
                    const pct = () => s().total === 0 ? 0 : Math.round(((s().drained + s().failed) / s().total) * 100);
                    const phaseLabel = (phase: string) => {
                        switch (phase) {
                            case 'Stopping':              return 'BOT_SHUTDOWN_PHASE_STOPPING';
                            case 'WaitingBeforeNextBot':  return 'BOT_SHUTDOWN_PHASE_WAITING_NEXT';
                            default:                      return 'BOT_SHUTDOWN_PHASE_STOPPING';
                        }
                    };
                    const secondsUntil = (iso: string | null): string => {
                        if (!iso) return '';
                        const diff = Math.max(0, Math.round((new Date(iso).getTime() - now()) / 1000));
                        return `${diff}s`;
                    };
                    const outcomeClass = (outcome: string) => {
                        if (outcome === 'Drained') return styles.autoResumeOutcomeSuccess;
                        return styles.autoResumeOutcomeFailed;
                    };

                    return (
                        <div class={styles.autoResumeCard}>
                            <div class={styles.autoResumeHeader}>
                                <h2 style={{ margin: 0 }}>
                                    <i class="fa-solid fa-power-off" style={{ 'margin-right': '8px' }} />
                                    <Text val="BOT_SHUTDOWN_TITLE" />
                                </h2>
                                <Show when={s().isShuttingDown} fallback={
                                    <span class={styles.autoResumePill} data-state="done">
                                        <i class="fa-solid fa-circle-check" />
                                        <Text val="BOT_SHUTDOWN_DONE" />
                                    </span>
                                }>
                                    <span class={styles.autoResumePill} data-state="running">
                                        <i class="fa-solid fa-spinner fa-spin" />
                                        <Text val="BOT_SHUTDOWN_RUNNING" />
                                    </span>
                                </Show>
                            </div>

                            <div class={styles.autoResumeCounters}>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--dark-text-primary)' }}>
                                        {s().total}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_SHUTDOWN_TOTAL" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--success-500)' }}>
                                        {s().drained}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_SHUTDOWN_DRAINED" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--error-500)' }}>
                                        {s().failed}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_SHUTDOWN_FAILED" /></div>
                                </div>
                                <div>
                                    <div class={styles.autoResumeCounterValue} style={{ color: 'var(--dark-text-secondary)' }}>
                                        {s().pending}
                                    </div>
                                    <div class={styles.autoResumeCounterLabel}><Text val="BOT_SHUTDOWN_PENDING" /></div>
                                </div>
                            </div>

                            <div class={styles.autoResumeProgress}>
                                <div class={styles.autoResumeProgressFill} style={{ width: `${pct()}%` }} />
                            </div>

                            <Show when={s().current}>
                                {(cur) => (
                                    <div class={styles.autoResumeCurrent}>
                                        <div>
                                            <span class={styles.autoResumeCurrentLabel}><Text val="BOT_SHUTDOWN_CURRENT" /></span>
                                            <span class={styles.autoResumeCurrentValue}>
                                                {cur().minecraftUsername}
                                                <span class={styles.autoResumeAttempt}>
                                                    {' '}({cur().order}/{cur().total})
                                                </span>
                                            </span>
                                        </div>
                                        <div class={styles.autoResumePhase}>
                                            <Text val={phaseLabel(cur().phase)} />
                                            <Show when={cur().nextActionAt}>
                                                {' · '}{secondsUntil(cur().nextActionAt)}
                                            </Show>
                                        </div>
                                    </div>
                                )}
                            </Show>

                            <Show when={s().recent.length > 0}>
                                <details class={styles.autoResumeRecent}>
                                    <summary><Text val="BOT_SHUTDOWN_RECENT" /> ({s().recent.length})</summary>
                                    <div class={styles.autoResumeRecentList}>
                                        <For each={[...s().recent].reverse()}>
                                            {(o) => (
                                                <div class={`${styles.autoResumeRecentRow} ${outcomeClass(o.outcome)}`}>
                                                    <span class={styles.autoResumeRecentUser}>{o.minecraftUsername}</span>
                                                    <span class={styles.autoResumeRecentOutcome}>{o.outcome}</span>
                                                    <span class={styles.autoResumeRecentMeta}>
                                                        <Show when={o.reason}>{o.reason}</Show>
                                                    </span>
                                                </div>
                                            )}
                                        </For>
                                    </div>
                                </details>
                            </Show>

                            <div class={styles.autoResumeRepassHint}>
                                <i class="fa-solid fa-rotate" />
                                <Text val="BOT_SHUTDOWN_RESUME_HINT" />
                            </div>
                        </div>
                    );
                })()}
            </Show>

            <Show when={broadcasterStats.length > 0}>
                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-sm)' }}>
                        <h2 style={{ margin: 0 }}>
                            <i class="fa-solid fa-tower-broadcast" style={{ 'margin-right': 'var(--spacing-sm)' }} />
                            <Text val="VEKTOR_POSSESSION_STATS_TITLE" />
                        </h2>
                        <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                            {broadcasterStats.length}
                        </span>
                    </div>

                    <div style={{ display: 'grid', 'grid-template-columns': 'minmax(8rem, 1fr) repeat(7, max-content)', gap: 'var(--spacing-sm) var(--spacing-lg)', 'align-items': 'center', 'font-size': 'var(--font-size-sm)' }}>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em' }}><Text val="VEKTOR_POSSESSION_STATS_COL_BOT" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_FC" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_RO" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_DEPTH" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_PKT" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_BYTES" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_POS" /></span>
                        <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)', 'text-transform': 'uppercase', 'letter-spacing': '0.04em', 'text-align': 'right' }}><Text val="VEKTOR_POSSESSION_STATS_COL_DROPPED" /></span>

                        <For each={broadcasterStats}>
                            {(b) => (
                                <>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)' }} title={b.botId}>
                                        {b.botId.slice(0, 8)}...
                                    </span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right' }}>{b.subscribersFullControl}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right' }}>{b.subscribersReadOnly}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right', color: queueColor(b.maxChannelDepth) }}>{b.maxChannelDepth}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right' }}>{b.packetsForwardedTotal.toLocaleString('de-DE')}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right' }}>{fmtBytes(b.bytesForwardedTotal)}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right' }}>{b.positionBroadcastsTotal.toLocaleString('de-DE')}</span>
                                    <span style={{ 'font-family': 'var(--font-family-mono)', 'text-align': 'right', color: b.packetsDroppedTotal > 0 ? 'var(--error-500)' : 'var(--dark-text-primary)' }}>{b.packetsDroppedTotal}</span>
                                </>
                            )}
                        </For>
                    </div>

                    <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'display': 'flex', 'align-items': 'center', gap: 'var(--spacing-xs)' }}>
                        <i class="fa-solid fa-circle-info" />
                        <Text val="VEKTOR_POSSESSION_STATS_HINT" />
                    </div>
                </div>
            </Show>

            <Show
                when={stats()}
                fallback={
                    <div class="fcard v" style={{ 'text-align': 'center', padding: '40px' }}>
                        <p style={{ color: 'var(--dark-text-muted)' }}>
                            <Show when={online()} fallback="Waiting for backend...">
                                Loading stats...
                            </Show>
                        </p>
                    </div>
                }
            >
                <div class={styles.statsSection}>
                    <div style={{
                        display: 'flex',
                        'justify-content': 'space-between',
                        'align-items': 'center',
                        'flex-wrap': 'wrap',
                        gap: 'var(--spacing-sm)',
                        'margin-bottom': 'var(--spacing-md)',
                    }}>
                        <h2 style={{ margin: 0 }}>Engine Overview</h2>
                        <span style={{
                            'font-size': 'var(--font-size-xs)',
                            color: 'var(--dark-text-muted)',
                            'font-variant-numeric': 'tabular-nums',
                        }}>
                            Updated {agoText()}
                        </span>
                    </div>

                    <div class={styles.statsGrid}>
                        <div class={styles.statCard}>
                            <div class={styles.statValue}>{stats()!.workerCount}</div>
                            <div class={styles.statLabel}>Workers</div>
                        </div>
                        <div class={styles.statCard}>
                            <div class={styles.statValue}>{stats()!.totalBots}</div>
                            <div class={styles.statLabel}>Active Bots</div>
                        </div>
                        <div class={styles.statCard}>
                            <div
                                class={styles.statValue}
                                style={{ color: worstRealTps() == null ? 'var(--dark-text-muted)' : realTpsColor(worstRealTps()!) }}
                                title="Lowest real ticks/sec across workers (measured from inter-tick gap, not work duration)"
                            >
                                {worstRealTps() == null ? '-' : fmt(worstRealTps()!, 1)}
                            </div>
                            <div class={styles.statLabel}>Real TPS</div>
                        </div>
                        <div class={styles.statCard}>
                            <div class={styles.statValue}>{stats()!.totalPacketsPerSecond}</div>
                            <div class={styles.statLabel}>Packets/sec</div>
                        </div>
                        <div class={styles.statCard}>
                            <div class={styles.statValue} style={{ color: queueColor(stats()!.totalInboundQueueDepth) }}>
                                {stats()!.totalInboundQueueDepth}
                            </div>
                            <div class={styles.statLabel}>Queue Depth</div>
                        </div>
                        <div class={styles.statCard}>
                            <div class={styles.statValue}>{stats()!.uptime}</div>
                            <div class={styles.statLabel}>Uptime</div>
                        </div>
                        <Show when={stats()!.botsReconnecting > 0}>
                            <div class={styles.statCard}>
                                <div class={styles.statValue} style={{ color: '#f59e0b' }}>
                                    {stats()!.botsReconnecting}
                                </div>
                                <div class={styles.statLabel}>Reconnecting</div>
                            </div>
                        </Show>
                    </div>

                    <div style={{
                        'margin-top': 'var(--spacing-md)',
                        padding: '12px 14px',
                        background: 'var(--dark-bg-secondary)',
                        border: '1px solid var(--dark-border)',
                        'border-radius': 'var(--radius-lg)',
                    }}>
                        <div style={{
                            display: 'flex',
                            'align-items': 'center',
                            'justify-content': 'space-between',
                            'flex-wrap': 'wrap',
                            gap: 'var(--spacing-xs)',
                            'margin-bottom': '6px',
                            'font-size': '11px',
                            color: 'var(--dark-text-muted)',
                            'text-transform': 'uppercase',
                            'font-weight': 'bold',
                            'letter-spacing': '0.5px',
                        }}>
                            <span>System Queue Depth · last 15 min</span>
                            <span style={{
                                display: 'inline-flex',
                                'align-items': 'center',
                                gap: '12px',
                                'text-transform': 'none',
                                'font-weight': 'normal',
                                'letter-spacing': 'normal',
                            }}>
                                <span style={{
                                    display: 'inline-flex',
                                    'align-items': 'center',
                                    gap: '5px',
                                    'font-size': '11px',
                                    color: 'var(--dark-text-muted)',
                                }}>
                                    <span style={{
                                        width: '10px',
                                        height: '3px',
                                        'border-radius': '2px',
                                        background: 'var(--accent-purple, #7c4dff)',
                                        display: 'inline-block',
                                        'flex-shrink': '0',
                                    }} />
                                    Queue Depth
                                </span>
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <div style={{
                                display: 'flex',
                                'flex-direction': 'column',
                                'justify-content': 'space-between',
                                'align-items': 'flex-end',
                                'font-size': '11px',
                                'font-family': 'monospace',
                                color: 'var(--dark-text-secondary)',
                                'min-width': '32px',
                                height: '120px',
                                'flex-shrink': '0',
                            }}>
                                <span>{queueYMax()}</span>
                                <span>0</span>
                            </div>
                            <div style={{ flex: '1', 'min-width': '0' }}>
                                <Sparkline
                                    data={systemQueue()}
                                    width={1200}
                                    height={120}
                                    capacity={HISTORY_MAX}
                                    fillWidth
                                    color="var(--accent-purple, #7c4dff)"
                                    fill="var(--accent-purple, #7c4dff)"
                                    samplesPerSecond={1}
                                    hideAxisLabel
                                    animated={false}
                                />
                            </div>
                        </div>
                    </div>

                    <h3 style={{ color: 'var(--dark-text-primary)', margin: '0 0 var(--spacing-sm) 0' }}>Workers</h3>
                    <table class={styles.workerTable}>
                        <thead>
                            <tr>
                                <th style={{ width: '7%' }}>ID</th>
                                <th style={{ width: '8%' }}>Bots</th>
                                <th style={{ width: '11%' }}>Real TPS</th>
                                <th style={{ width: '22%' }}>Gap avg/p95/max</th>
                                <th style={{ width: '17%' }}>Oversleep avg/p95</th>
                                <th style={{ width: '11%' }}>Avg Tick</th>
                                <th style={{ width: '10%' }}>Peak</th>
                                <th style={{ width: '14%' }}>Budget</th>
                            </tr>
                        </thead>
                        <tbody>
                            <For each={stats()!.workers}>{(w) =>
                                <tr>
                                    <td>#{w.workerId}</td>
                                    <td>{w.botCount}</td>
                                    <td style={{ color: realTpsColor(w.realTps), 'font-weight': 'bold' }}>
                                        {fmt(w.realTps, 1)}
                                    </td>
                                    <td
                                        style={{ color: gapColor(w.gapP95Ms) }}
                                        title={`avg ${fmt(w.gapAvgMs, 1)}ms · p95 ${fmt(w.gapP95Ms, 1)}ms · max ${fmt(w.gapMaxMs, 1)}ms (scheduled 50ms)`}
                                    >
                                        {fmt(w.gapAvgMs, 1)} / {fmt(w.gapP95Ms, 1)} / {fmt(w.gapMaxMs, 1)}ms
                                    </td>
                                    <td
                                        style={{ color: oversleepColor(w.oversleepP95Ms) }}
                                        title={`avg ${fmt(w.oversleepAvgMs, 1)}ms · p95 ${fmt(w.oversleepP95Ms, 1)}ms: kernel wait beyond requested sleep`}
                                    >
                                        {fmt(w.oversleepAvgMs, 1)} / {fmt(w.oversleepP95Ms, 1)}ms
                                    </td>
                                    <td class={w.isOverloaded ? styles.overloaded : ''}>{w.averageTickMs}ms</td>
                                    <td>{w.peakTickMs}ms</td>
                                    <td>
                                        {w.tickBudgetUsage}%
                                        <div class={styles.budgetBar}>
                                            <div class={styles.budgetFill} style={{
                                                width: `${Math.min(100, w.tickBudgetUsage)}%`,
                                                background: 'var(--primary-400)',
                                            }} />
                                        </div>
                                    </td>
                                </tr>
                            }</For>
                        </tbody>
                    </table>

                    <Show when={stats()!.bots.length > 0}>
                        <h3 style={{ color: 'var(--dark-text-primary)', margin: 'var(--spacing-xl) 0 var(--spacing-sm) 0' }}>
                            Connected Bots
                        </h3>
                        <div class={styles.botHealthGrid}>
                            <For each={stats()!.bots}>{(b) =>
                                <BotHealthCard bot={b} />
                            }</For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
